import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Building2, Shield, CheckCircle2, Eye } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import LanguageToggle from '../components/LanguageToggle';

const LandingPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [showRegister, setShowRegister] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchTerms = async () => {
    try {
      const response = await axios.get(`${API}/auth/terms`);
      setTerms(response.data);
    } catch (error) {
      toast.error(t('messages.failedToLoad') + ' terms');
    }
  };

  const handleGetStarted = () => {
    setShowRegister(true);
    fetchTerms();
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!termsAccepted) {
      toast.error(t('auth.pleaseAcceptTerms'));
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/register`, { email, name });
      toast.success(t('auth.registrationSuccess'));
      setTimeout(() => navigate('/login'), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (showRegister) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center p-4">
        <div className="absolute top-4 right-4">
          <LanguageToggle />
        </div>
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full">
          <div className="text-center mb-8">
            <Building2 className="h-12 w-12 text-blue-600 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900">{t('auth.registerTitle')}</h2>
            <p className="text-gray-600 mt-2">{t('auth.registerSubtitle')}</p>
          </div>

          <form onSubmit={handleRegister} className="space-y-6">
            <div>
              <Label htmlFor="email" data-testid="register-email-label">{t('email')}</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
                placeholder={t('auth.emailPlaceholder')}
                data-testid="register-email-input"
              />
            </div>

            <div>
              <Label htmlFor="name" data-testid="register-name-label">{t('name')}</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="mt-1"
                placeholder={t('auth.namePlaceholder')}
                data-testid="register-name-input"
              />
            </div>

            {terms && (
              <div className="border border-gray-300 rounded p-4 max-h-48 overflow-y-auto bg-gray-50">
                <h4 className="font-semibold mb-2 text-sm">{t('auth.termsTitle')}</h4>
                <p className="text-xs text-gray-700 whitespace-pre-line">{terms.content}</p>
              </div>
            )}

            <div className="flex items-start">
              <input
                type="checkbox"
                id="terms"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="mt-1 mr-2"
                data-testid="accept-terms-checkbox"
              />
              <Label htmlFor="terms" className="text-sm text-gray-700 cursor-pointer">
                {t('auth.acceptTerms')}
              </Label>
            </div>

            <Button
              type="submit"
              className="w-full btn-corporate"
              disabled={loading}
              data-testid="register-submit-button"
            >
              {loading ? t('auth.registering') : t('register')}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="text-blue-600 hover:underline text-sm"
                data-testid="go-to-login-link"
              >
                {t('auth.alreadyHaveAccount')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-2">
            <Building2 className="h-8 w-8 text-blue-600" />
            <span className="text-xl font-bold text-gray-900">MassPayHub</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageToggle />
            <Button
              onClick={() => navigate('/login')}
              variant="outline"
              data-testid="header-login-button"
            >
              {t('landing.login')}
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="corporate-gradient text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6">
            {t('landing.title')}
          </h1>
          <p className="text-lg sm:text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
            {t('landing.subtitle')}
          </p>
          <Button
            size="lg"
            className="bg-white text-blue-600 hover:bg-gray-100 font-semibold px-8 py-6 text-lg"
            onClick={handleGetStarted}
            data-testid="get-started-button"
          >
            {t('landing.getStarted')}
          </Button>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            {t('landing.features.title')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="data-card text-center">
              <Shield className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t('landing.features.secure.title')}</h3>
              <p className="text-gray-600 text-sm">
                {t('landing.features.secure.desc')}
              </p>
            </div>
            <div className="data-card text-center">
              <CheckCircle2 className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t('landing.features.convenient.title')}</h3>
              <p className="text-gray-600 text-sm">
                {t('landing.features.convenient.desc')}
              </p>
            </div>
            <div className="data-card text-center">
              <Eye className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">{t('landing.features.transparent.title')}</h3>
              <p className="text-gray-600 text-sm">
                {t('landing.features.transparent.desc')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400 text-sm">
            {t('landing.footer')}
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
